/* eslint-disable react/sort-comp */
import React from 'react';

export interface IWithOverflowState {
  overflow: boolean;
}

export interface IWithOverflowInjectedProps {
  forwardedRef: React.RefObject<HTMLElement>;
  isTextOverflow: boolean;
}

export type WithOverFlowType<P> = Omit<P, keyof IWithOverflowInjectedProps>;

/* istanbul ignore next */
function isIEBrowser(): boolean {
  const userAgent = window.navigator && window.navigator.userAgent;
  const isIE = userAgent.indexOf('compatible') > -1 && userAgent.indexOf('MSIE') > -1;
  const isEdge = userAgent.indexOf('Edge') > -1;
  const isIE11 = userAgent.indexOf('Trident') > -1 && userAgent.indexOf('rv:11.0') > -1;

  return isIE || isEdge || isIE11;
}

function isTextOverflow(element: HTMLElement): boolean {
  // this is a workaround to fix the issue with IE scrollWidth
  // which the scrollWidth is off by 1px compared to the offsetWidth
  /* istanbul ignore next */
  const clientWidth = isIEBrowser() ? element.clientWidth + 1 : element.clientWidth;

  return clientWidth < element.scrollWidth;
}

export const withOverflowProp = <P extends IWithOverflowInjectedProps>(
  WrappedComponent: React.ComponentType<P>
): React.ComponentType<WithOverFlowType<P>> => class WithOverflowProp extends React.Component<WithOverFlowType<P>, IWithOverflowState> {
    forwardedRef: React.RefObject<HTMLElement>;

    constructor(props: WithOverFlowType<P>) {
      super(props);

      // TODO: React Router still supports React 16.3.2. So we are not able to use
      // `forwardRef` right now. The fix will be in version 5 when React Router upgrade
      // their dependency to React 16.7+. Check and change to use forwardRef until then.
      this.forwardedRef = React.createRef<HTMLElement>();

      this.state = {
        overflow: false,
      };
    }

    componentDidMount(): void {
      this.checkOverflow();
    }

    componentDidUpdate(): void {
      this.checkOverflow();
    }

    checkOverflow = (): void => {
      const element = this.forwardedRef.current;

      const overflow = isTextOverflow(element);
      if (overflow !== this.state.overflow) {
        this.setState({ overflow });
      }
    };

    render = (): React.ReactNode => (

      <WrappedComponent
        forwardedRef={this.forwardedRef}
        isTextOverflow={this.state.overflow}
        {...this.props as P}
      />
    );
  };
